import React, { useState, useCallback, useMemo, useContext } from 'react';
import styled from 'styled-components';
import { setWith } from 'lodash';
import useClient from '@ubeya/shared/hooks/account/useClient';
import {
  Loader as BaseLoader,
  Avatar as BaseAvatar,
  FormModal as BaseFormModal,
  OrdersTabDetails,
  SmallBody
} from '@ubeya/shared-web/components';
import { getDirtyFields, splitDirtyArrayToStatus } from '@ubeya/shared/utils/form';
import useToaster from '@ubeya/shared/hooks/useToaster';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import { BranchesTab } from '@ubeya/shared-web/components';
import useClientBranchesAccess from '@ubeya/shared/hooks/account/useClientBranchesAccess';
import {
  ALPHA_ACCOUNT_ID,
  CONSTELATION_ACCOUNT_ID,
  CONSTELLATION_IRELAND_ACCOUNT_ID,
  DROR_DEMO_ACCOUNT_ID
} from '@ubeya/shared/utils/accounts';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import privileges from '@ubeya/shared-web/privileges';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import ClientCardTabs from './ClientCardTabs';
import TabContent from './TabContent';
import Profile from './Profile';
import Contacts from './Contacts';
import Rates from './Rates';
import ClientInvoices from './Invoices/ClientInvoices';
import ClientAWR from './AWR';
import { OrdersTab } from './ClientCardOrdersTab/OrdersTab';
import { LocationsTab } from './LocationsTab';
import { CommentsTab } from './CommentsTab';
import { InvoicingTab } from './InvoicingTab/InvoicingTab';
import ClientCardCostCentersTab from './ClientCardCostCentersTab';
import { ACCOUNT_PAYING_STATUSES, TabTypes } from 'crm/constants';

const Loader = styled(BaseLoader)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const FormModal = styled(BaseFormModal)`
  .content {
    overflow: hidden;
  }
`;

const OrderTabDetailsContainer = styled.div`
  z-index: 3;
`;

const Avatar = styled(BaseAvatar)`
  margin-right: 10px;
`;
const Container = styled.div`
  display: flex;
  align-items: center;
`;

const Message = styled(SmallBody)`
  padding: 10px 0;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.black};
  margin-right: auto;
  margin-left: 40px;
`;

const formatItemWithWage = (item) => ({
  ...item,
  ...(item.wage && {
    wage: {
      hourlyWage: item.wage.hourlyWage === '' ? undefined : item.wage.hourlyWage,
      flatWage: item.wage.flatWage === '' ? undefined : item.wage.flatWage,
      flatHours: item.wage.flatHours === '' ? undefined : item.wage.flatHours
    }
  }),
  ...(item.payrollWage && {
    payrollWage: {
      hourlyWage: item.payrollWage.hourlyWage === '' ? undefined : item.payrollWage.hourlyWage,
      flatWage: item.payrollWage.flatWage === '' ? undefined : item.payrollWage.flatWage,
      flatHours: item.payrollWage.flatHours === '' ? undefined : item.payrollWage.flatHours
    }
  })
});

// SEND TO BACKEND WITH WAGE AND PAYROLL WAGE
const formatWagesItemWithWage = (item) => {
  const formattedClientWage = item.wage
    ? {
        clientWage: {
          hourlyWage: item.wage.hourlyWage === '' ? undefined : item.wage.hourlyWage,
          flatWage: item.wage.flatWage === '' ? undefined : item.wage.flatWage,
          flatHours: item.wage.flatHours === '' ? undefined : item.wage.flatHours
        }
      }
    : {};

  const formattedWage = item.payrollWage
    ? {
        wage: {
          hourlyWage: item.payrollWage.hourlyWage === '' ? undefined : item.payrollWage.hourlyWage,
          flatWage: item.payrollWage.flatWage === '' ? undefined : item.payrollWage.flatWage,
          flatHours: item.payrollWage.flatHours === '' ? undefined : item.payrollWage.flatHours
        }
      }
    : {};

  // do not send payrollWage to backend
  const { payrollWage: _payrollWage, ...restItem } = item;

  return {
    ...restItem,
    ...formattedClientWage,
    ...formattedWage
  };
};

const ClientCard = ({ defaultData, onClose, onConfirm = onClose, defaultActiveTab = TabTypes.SETTINGS }) => {
  const { t } = useContext(ConfigContext);
  const [currentOrder, setCurrentOrder] = useState();

  const myPrivileges = useContext(PrivilegesContext);

  const { accountId, config } = useAccount();
  const { isUbeyaAdmin } = useUser();
  const { account: accountConfig } = useAccountConfig();

  const clientId = defaultData?.id;

  const { inviteClientAdmin } = useCRM();
  const { loading, data, updateClient, deleteClient } = useClient({ clientId });

  const clientData = useMemo(
    () => ({
      ...data,
      payrollFields: (data?.payrollFields || []).reduce((output, current) => {
        output[`payroll-${current.payrollFieldId}`] = current;
        return output;
      }, {})
    }),
    [data]
  );

  const initialValues = useMemo(() => {
    const values = {
      ...defaultData,
      ...clientData
    };

    if (values?.allowClientToBlockEmployees === undefined) {
      values.allowClientToBlockEmployees = accountConfig?.crmConfig?.allowClientsToBlockEmployees;
    }

    return values;
  }, [accountConfig?.crmConfig?.allowClientsToBlockEmployees, clientData, defaultData]);

  const allowClientBillingInfo = useMemo(() => (clientId ? myPrivileges[privileges.CLIENT_BILLING] || false : false), [
    clientId,
    myPrivileges
  ]);

  const showCostCenterTab = initialValues.isClientFreemium;

  const { clientCardTabs, defaultTab } = useMemo(() => {
    const tabs = [TabTypes.SETTINGS, TabTypes.CONTACTS];

    if (allowClientBillingInfo) {
      tabs.push(TabTypes.FINANCIALS, TabTypes.RATES);
    }

    if (accountId === ALPHA_ACCOUNT_ID || accountId === DROR_DEMO_ACCOUNT_ID) {
      tabs.push(TabTypes.AWR);
    }

    if (clientId) {
      tabs.push(TabTypes.ORDERS, TabTypes.LOCATIONS, TabTypes.COMMENTS);
    }

    if (isUbeyaAdmin && clientId) {
      tabs.push(TabTypes.BRANCHES);
    }

    if (myPrivileges[privileges.INVOICE] && clientId) {
      tabs.push(TabTypes.INVOICES);
    }

    if (showCostCenterTab) {
      tabs.push(TabTypes.CLIENT_COST_CENTERS);
    }

    const finalDefaultTab = tabs.includes(defaultActiveTab) ? defaultActiveTab : TabTypes.SETTINGS;

    return { clientCardTabs: tabs, defaultTab: finalDefaultTab };
  }, [accountId, allowClientBillingInfo, clientId, defaultActiveTab, isUbeyaAdmin, myPrivileges, showCostCenterTab]);

  const [activeTab, setActiveTab] = useState(defaultTab);

  const isAccountPaying = ACCOUNT_PAYING_STATUSES.includes(config.billingStatus);

  const enableFieldsEdit =
    (data?.isClientEditable && isAccountPaying) ||
    accountId === CONSTELATION_ACCOUNT_ID ||
    accountId === CONSTELLATION_IRELAND_ACCOUNT_ID;

  const { updateConfiguration } = useClientBranchesAccess({ clientId });

  const clientBranchesIds = (data?.branches || []).map(({ id }) => id);

  const { addResponseError, addSuccess } = useToaster();

  const handleSubmit = useCallback(
    async (values, form) => {
      const { branchEnabled, clientBranchesEnabled, ...initialClientValues } = values;

      let clientValues = initialClientValues;

      if (clientId) {
        const { dirtyFields } = form.getState();
        const {
          excludedPositions: excludedPositionsIsDirty,
          positionsWages: positionsIsDirty,
          ...restDirty
        } = dirtyFields;
        const payrollFieldsIsDirty = Object.entries(restDirty).some(([key]) => {
          return key.indexOf('payrollFields') === 0;
        });
        const restDirtyFields = Object.fromEntries(
          Object.entries(restDirty).filter(([key]) => {
            return key.indexOf('payrollFields') !== 0;
          })
        );

        const formattedPositions = values.positionsWages
          // IF THERE'S STATUS, THEN IT'S DIRTY
          .filter(({ status }) => !!status)
          .map((positionItem) => {
            const formattedItem = formatItemWithWage(positionItem);
            const wages = (formattedItem.wages || []).map((item) => formatWagesItemWithWage(item));

            const byStatusWages = splitDirtyArrayToStatus('wages', { wages });

            return {
              ...formattedItem,
              wages: {
                added: byStatusWages?.added || [],
                edited: byStatusWages?.edited || [],
                deleted: byStatusWages?.deleted || []
              }
            };
          });

        clientValues = {
          ...Object.entries(getDirtyFields(values, restDirtyFields)).reduce(
            (carry, [key, value]) => setWith(carry, key, value),
            {}
          ),
          ...(positionsIsDirty && { positionsWages: formattedPositions }),
          //avoid the added, updated, deleted transformer
          ...(excludedPositionsIsDirty && { excludedPositions: values.excludedPositions }),
          ...(payrollFieldsIsDirty && {
            payrollFields: Object.entries(values.payrollFields).map(([key, { value }]) => ({
              payrollFieldId: Number(key?.split?.('-')?.[1]),
              value: Number(value)
            }))
          })
        };
      }

      let updateClientBranchesMapping = branchEnabled || clientBranchesEnabled;
      let configs = [];

      if (updateClientBranchesMapping) {
        Object.keys(branchEnabled).forEach((key) => {
          const branchId = parseInt(key.replace('branch_', ''));
          const enabledBranches = clientBranchesEnabled?.[key] || [];
          const accessType =
            branchEnabled[key] === true ? (enabledBranches.length > 0 ? 'custom' : 'enabled') : 'disabled';
          configs.push({
            branchId,
            accessType,
            enabledBranches
          });
        });
      }

      try {
        const clientUpdatedData = await updateClient(clientValues);
        if (updateClientBranchesMapping) {
          await updateConfiguration(configs);
        }
        onConfirm(clientUpdatedData);
      } catch (err) {
        addResponseError(err);
      }
    },
    [addResponseError, clientId, onConfirm, updateClient, updateConfiguration]
  );

  const inviteAdmin = useCallback(
    (adminId) => {
      inviteClientAdmin({ accountId, clientId, adminId });
      addSuccess(t('mailWasSentSuccessfully'));
    },
    [accountId, addSuccess, clientId, inviteClientAdmin, t]
  );

  const title = defaultData?.id ? (
    <Container>
      <Avatar firstName={initialValues.name} image={initialValues.logo} size="big" />
      <div>{initialValues.name}</div>
    </Container>
  ) : (
    <div>{t('addClient')}</div>
  );

  const isClientEditable = defaultData?.id ? clientData?.isClientEditable : true;

  return (
    <FormModal
      disabledOnPristine={false}
      requiredWarningFooter={t('missingRequiredFields')}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      onSubmit={handleSubmit}
      size="xxlarge"
      onClose={onClose}
      title={title}
      confirmText={defaultData?.id ? 'save' : 'add'}
      sideDetails={
        currentOrder && (
          <OrderTabDetailsContainer>
            <OrdersTabDetails
              order={currentOrder}
              onClose={setCurrentOrder}
              isClient={currentOrder.clientAccountId === accountId}
            />
          </OrderTabDetailsContainer>
        )
      }
      showSideDetailsModal={!!currentOrder}
      renderButtons={() =>
        activeTab === TabTypes.CONTACTS && <Message>{t('crmClientContactIsNotShownToEmployees')}</Message>
      }
      isPaddingContent={false}>
      {({ submitting }) => (
        <>
          <ClientCardTabs
            tabs={clientCardTabs}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            deleteClient={deleteClient}
            handleClose={onClose}
            accountId={accountId}
            clientId={clientId}
          />

          {loading && <Loader />}

          <TabContent submitting={submitting} activeTab={activeTab}>
            <Profile activeTab={TabTypes.SETTINGS} clientId={clientId} isClientEditable={isClientEditable} />
            <LocationsTab activeTab={TabTypes.LOCATIONS} clientId={clientId} disableFieldsEdit={!enableFieldsEdit} />
            <CommentsTab activeTab={TabTypes.COMMENTS} clientId={clientId} />

            {myPrivileges[privileges.INVOICE] && activeTab === TabTypes.INVOICES && (
              <InvoicingTab activeTab={TabTypes.INVOICES} clientId={clientId} />
            )}

            <Contacts
              activeTab={TabTypes.CONTACTS}
              inviteAdmin={inviteAdmin}
              initialValues={initialValues}
              isClientEditable={isClientEditable}
              clientId={clientId}
              clientOrganizationId={data?.organizationId}
            />

            <Rates activeTab={TabTypes.RATES} />

            <ClientInvoices
              activeTab={TabTypes.FINANCIALS}
              clientId={clientId}
              clientName={initialValues.name}
              clientBranchesIds={clientBranchesIds}
              isShown={activeTab === TabTypes.FINANCIALS}
            />

            <ClientAWR activeTab={TabTypes.AWR} clientId={clientId} />

            {showCostCenterTab && (
              <ClientCardCostCentersTab
                activeTab={TabTypes.CLIENT_COST_CENTERS}
                clientId={clientId}
                clientBranches={initialValues?.branches || []}
              />
            )}

            {activeTab === TabTypes.ORDERS && (
              <OrdersTab
                activeTab={TabTypes.ORDERS}
                clientId={clientId}
                onOpenOrderDetails={setCurrentOrder}
                isActive
              />
            )}
            {activeTab === TabTypes.BRANCHES && isUbeyaAdmin && (
              <BranchesTab activeTab={TabTypes.BRANCHES} clientId={clientId} />
            )}
          </TabContent>
        </>
      )}
    </FormModal>
  );
};

export default ClientCard;
